/**
 * Éditeur de contenu éditorial : TipTap, à barre d'outils volontairement pauvre.
 *
 * Titres (h2, h3), gras, italique, listes, liens. Rien d'autre — et surtout,
 * rien qui produirait une balise que `assainirHtml` retirerait ensuite.
 *
 * Ce n'est pas une mesure de sécurité : l'API réassainit systématiquement ce
 * qu'elle reçoit, et c'est elle qui protège le site. C'est une mesure
 * d'honnêteté d'interface. Un éditeur permissif laisserait mettre en forme du
 * texte souligné, barré, coloré ou tabulaire, puis l'enregistrerait
 * silencieusement amputé : l'utilisateur relirait sa page publiée sans y
 * retrouver ce qu'il avait vu à l'écran. Mieux vaut ne jamais proposer le
 * bouton que de renier ce qu'il a fait.
 *
 * Le seul balisage produit ici est donc : h2, h3, p, ul, ol, li, strong, em, a.
 * C'est exactement `BALISES_AUTORISEES` de `@delsa/shared`.
 */
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { OR } from "../theme";

/**
 * Schémas de lien acceptés, alignés sur `SCHEMES_LIEN_AUTORISES` côté serveur.
 * Un lien saisi avec un autre schéma serait dépouillé de son `href` à
 * l'enregistrement ; autant le refuser tout de suite, en le disant.
 */
const SCHEMES_AUTORISES = ["http:", "https:", "mailto:", "tel:"] as const;

/** `true` si l'URL survivra à l'assainissement du serveur. */
export function lienAcceptable(brut: string): boolean {
  const valeur = brut.trim();
  if (valeur === "") return false;
  // Lien interne ou ancre : aucun schéma à contrôler.
  if (valeur.startsWith("/") || valeur.startsWith("#")) return true;

  const schema = /^[a-z][a-z0-9+.-]*:/i.exec(valeur);
  if (schema === null) return false;
  const trouve = schema[0].toLowerCase();
  return SCHEMES_AUTORISES.some((autorise) => autorise === trouve);
}

/**
 * Extensions : tout ce que la liste blanche n'accepte pas est désactivé
 * explicitement, plutôt que laissé au hasard des évolutions de StarterKit.
 */
const EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    // `h1` est réservé au titre de la page, produit par le gabarit Astro : deux
    // `h1` dans une page en cassent la structure sémantique, donc le référencement.
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    strike: false,
    underline: false,
    // Un `<br>` ne figure pas dans la liste blanche : il disparaîtrait à
    // l'enregistrement et les deux lignes se retrouveraient collées.
    hardBreak: false,
    link: {
      openOnClick: false,
      autolink: false,
      linkOnPaste: false,
      protocols: ["mailto", "tel"],
      // Le serveur ne conserve que `href` sur une balise `a` : poser ici un
      // `target` ou un `rel` afficherait un attribut que la page publiée n'aura pas.
      HTMLAttributes: {},
    },
  }),
];

type ProprietesEditeur = {
  /** Étiquette du champ, reprise par le lecteur d'écran sur la zone d'édition. */
  libelle: string;
  valeur: string;
  surChangement: (html: string) => void;
  /** Texte d'aide affiché sous l'éditeur. */
  aide?: string;
  /** Hauteur minimale de la zone de saisie, en pixels. */
  hauteurMin?: number;
  desactive?: boolean;
};

export function EditeurRiche({
  libelle,
  valeur,
  surChangement,
  aide,
  hauteurMin = 220,
  desactive = false,
}: ProprietesEditeur) {
  const [dialogueLien, setDialogueLien] = useState(false);
  const [urlSaisie, setUrlSaisie] = useState("");
  const [erreurLien, setErreurLien] = useState<string | null>(null);

  const editeur = useEditor({
    extensions: EXTENSIONS,
    content: valeur,
    editable: !desactive,
    onUpdate: ({ editor }) => {
      // TipTap rend « <p></p> » pour un document vide. Le renvoyer tel quel
      // ferait compter un paragraphe fantôme comme du contenu.
      const html = editor.getHTML();
      surChangement(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        "aria-label": libelle,
        role: "textbox",
        "aria-multiline": "true",
      },
    },
  });

  /**
   * Recharge le document quand la valeur change **hors** de l'éditeur : passage
   * d'une fiche à une autre, ou remise à zéro après enregistrement. La
   * comparaison au HTML courant est indispensable : sans elle, chaque frappe
   * réécrirait le document et renverrait le curseur au début.
   */
  useEffect(() => {
    if (!editeur) return;
    const courant = editeur.getHTML();
    const normalise = courant === "<p></p>" ? "" : courant;
    if (normalise === valeur) return;
    editeur.commands.setContent(valeur, { emitUpdate: false });
  }, [editeur, valeur]);

  useEffect(() => {
    editeur?.setEditable(!desactive);
  }, [editeur, desactive]);

  const etat = useEditorState({
    editor: editeur,
    selector: ({ editor }) => ({
      titre2: editor?.isActive("heading", { level: 2 }) ?? false,
      titre3: editor?.isActive("heading", { level: 3 }) ?? false,
      gras: editor?.isActive("bold") ?? false,
      italique: editor?.isActive("italic") ?? false,
      listePuces: editor?.isActive("bulletList") ?? false,
      listeNumeros: editor?.isActive("orderedList") ?? false,
      lien: editor?.isActive("link") ?? false,
    }),
  });

  function ouvrirDialogueLien() {
    if (!editeur) return;
    const existant = editeur.getAttributes("link").href;
    setUrlSaisie(typeof existant === "string" ? existant : "");
    setErreurLien(null);
    setDialogueLien(true);
  }

  function poserLien(evenement: FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    if (!editeur) return;

    const url = urlSaisie.trim();
    if (!lienAcceptable(url)) {
      setErreurLien(
        "Adresse refusée. Utilisez un lien interne (/contact/), une ancre (#devis) ou une adresse en http://, https://, mailto: ou tel:.",
      );
      return;
    }

    // `extendMarkRange` évite le piège classique : cliquer au milieu d'un lien
    // existant et n'en modifier qu'une partie, ce qui le couperait en deux.
    editeur.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    setDialogueLien(false);
  }

  function retirerLien() {
    editeur?.chain().focus().extendMarkRange("link").unsetLink().run();
  }

  if (!editeur) return null;

  type BoutonBarre = {
    cle: string;
    titre: string;
    contenu: ReactNode;
    actif: boolean;
    action: () => void;
    /** N'apparaît que lorsque le curseur est déjà dans un lien. */
    seulementSurLien?: boolean;
  };

  const boutons: BoutonBarre[] = [
    {
      cle: "titre2",
      titre: "Titre de niveau 2",
      contenu: "H2",
      actif: etat?.titre2 ?? false,
      action: () => editeur.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      cle: "titre3",
      titre: "Titre de niveau 3",
      contenu: "H3",
      actif: etat?.titre3 ?? false,
      action: () => editeur.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      cle: "gras",
      titre: "Gras",
      contenu: <FormatBoldIcon fontSize="small" />,
      actif: etat?.gras ?? false,
      action: () => editeur.chain().focus().toggleBold().run(),
    },
    {
      cle: "italique",
      titre: "Italique",
      contenu: <FormatItalicIcon fontSize="small" />,
      actif: etat?.italique ?? false,
      action: () => editeur.chain().focus().toggleItalic().run(),
    },
    {
      cle: "puces",
      titre: "Liste à puces",
      contenu: <FormatListBulletedIcon fontSize="small" />,
      actif: etat?.listePuces ?? false,
      action: () => editeur.chain().focus().toggleBulletList().run(),
    },
    {
      cle: "numeros",
      titre: "Liste numérotée",
      contenu: <FormatListNumberedIcon fontSize="small" />,
      actif: etat?.listeNumeros ?? false,
      action: () => editeur.chain().focus().toggleOrderedList().run(),
    },
    {
      cle: "lien",
      titre: "Insérer ou modifier un lien",
      contenu: <LinkIcon fontSize="small" />,
      actif: etat?.lien ?? false,
      action: ouvrirDialogueLien,
    },
    {
      cle: "sans-lien",
      titre: "Retirer le lien",
      contenu: <LinkOffIcon fontSize="small" />,
      actif: false,
      action: retirerLien,
      seulementSurLien: true,
    },
  ];

  return (
    <Box>
      <Typography variant="subtitle2" component="p" gutterBottom>
        {libelle}
      </Typography>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          backgroundColor: desactive ? "action.hover" : "background.paper",
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ p: 0.75, flexWrap: "wrap", rowGap: 0.5 }}
          role="toolbar"
          aria-label={`Mise en forme — ${libelle}`}
        >
          {boutons
            .filter((bouton) => !bouton.seulementSurLien || (etat?.lien ?? false))
            .map((bouton) => (
              <ToggleButton
                key={bouton.cle}
                value={bouton.cle}
                size="small"
                selected={bouton.actif}
                disabled={desactive}
                aria-label={bouton.titre}
                aria-pressed={bouton.actif}
                title={bouton.titre}
                onClick={bouton.action}
                sx={{ px: 1.25, py: 0.5, minWidth: 36, fontWeight: 700 }}
              >
                {bouton.contenu}
              </ToggleButton>
            ))}
        </Stack>

        <Divider />

        <Box
          sx={{
            px: 2,
            py: 1.5,
            "& .tiptap": {
              minHeight: hauteurMin,
              outline: "none",
            },
            "& .tiptap:focus-visible": {
              outline: `3px solid ${OR}`,
              outlineOffset: "2px",
              borderRadius: 1,
            },
            "& h2": { fontSize: "1.35rem", fontWeight: 700, mt: 2, mb: 1 },
            "& h3": { fontSize: "1.1rem", fontWeight: 700, mt: 2, mb: 1 },
            "& p": { my: 1, lineHeight: 1.7 },
            "& ul, & ol": { pl: 3, my: 1 },
            "& li": { mb: 0.5 },
            "& a": { color: "primary.main", textDecoration: "underline" },
          }}
        >
          <EditorContent editor={editeur} />
        </Box>
      </Box>

      {aide && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {aide}
        </Typography>
      )}

      <Dialog open={dialogueLien} onClose={() => setDialogueLien(false)} fullWidth maxWidth="sm">
        <form onSubmit={poserLien}>
          <DialogTitle>Adresse du lien</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }} component="div">
              Lien interne (<code>/contact/</code>), ancre (<code>#devis</code>) ou adresse complète
              en <code>http://</code>, <code>https://</code>, <code>mailto:</code> ou{" "}
              <code>tel:</code>. Tout autre schéma serait retiré à l'enregistrement.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="Adresse"
              value={urlSaisie}
              onChange={(evenement) => setUrlSaisie(evenement.target.value)}
              error={erreurLien !== null}
              helperText={erreurLien ?? " "}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogueLien(false)}>Annuler</Button>
            <Button type="submit" variant="contained">
              Appliquer
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
